import { HsTextureData } from './texture-data.class';
import type { HsTextureParameters } from './texture-parameters.interface';
import { HsTextureQuality } from './texture-quality.enum';

type TextureLoadPromiseResolveType = (value: HsTextureData | Error) => void;

export namespace HsTextureUtils {
  export function loadWithCanvas(url: string, context: CanvasRenderingContext2D): Promise<HsTextureData | Error> {
    const image: HTMLImageElement = new Image();
    const promise: Promise<HsTextureData | Error> = new Promise((resolve: TextureLoadPromiseResolveType) => {
      image.src = url;
      image.onload = () => {
        context.canvas.height = image.naturalHeight;
        context.canvas.width = image.naturalWidth;
        try {
          context.drawImage(image, 0, 0);
          const imageData: ImageData = context.getImageData(0, 0, image.naturalWidth, image.naturalHeight);
          const buffer: ArrayBufferLike = imageData.data.buffer;
          const textureData: HsTextureData = new HsTextureData(
            buffer,
            imageData.width,
            imageData.height,
            buffer.byteLength
          );
          image.remove();
          resolve(textureData);
        } catch (error: unknown) {
          if (error instanceof Error) {
            resolve(error);
            return;
          }
          const defaultError: Error = new Error(
            `ERROR: TextureUtils - Failed to create ImageData using canvas context`
          );
          resolve(defaultError);
        }
      };
      image.onerror = () => {
        const error: Error = new Error(`ERROR: TextureUtils - Failed to load texture image, url: ${url}`);
        resolve(error);
      };
    });
    return promise;
  }

  export function load(
    gl: WebGL2RenderingContext,
    url: string,
    needsMipmap: boolean = true,
    quality: HsTextureQuality = HsTextureQuality.Highest
  ): Promise<WebGLTexture | null> {
    const texture: WebGLTexture | null = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 255]));
    const image: HTMLImageElement = new Image();
    image.src = url;
    const promise: Promise<WebGLTexture | null> = new Promise((resolve: (value: WebGLTexture | null) => void) => {
      image.addEventListener('load', function () {
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        setTextureParameters(gl, needsMipmap, quality);
        resolve(texture);
      });
    });
    return promise;
  }

  export function createGraphicTexture(
    gl: WebGL2RenderingContext,
    { buffer, width, height }: HsTextureData,
    { needMipmap, textureQuality, level, internalFormat, border, format, type }: HsTextureParameters
  ): WebGLTexture | null {
    const data: Uint8Array = new Uint8Array(buffer);

    const texture: WebGLTexture | null = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);

    setTextureParameters(gl, needMipmap, textureQuality);

    gl.texImage2D(gl.TEXTURE_2D, level, internalFormat, width, height, border, format, type, data);

    gl.bindTexture(gl.TEXTURE_2D, null);

    return texture;
  }

  function setTextureParameters(gl: WebGL2RenderingContext, needsMipmap: boolean, quality: HsTextureQuality): void {
    if (needsMipmap) {
      switch (quality) {
        case HsTextureQuality.Low:
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_NEAREST);
          break;
        case HsTextureQuality.Medium:
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST_MIPMAP_LINEAR);
          break;
        case HsTextureQuality.High:
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_NEAREST);
          break;
        case HsTextureQuality.Highest:
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
          break;
        default:
          const neverQuality: never = quality;
          throw new Error(`ERROR: TextureUtils - Unknown texture quality value: ${neverQuality}`);
      }
      gl.generateMipmap(gl.TEXTURE_2D);
    } else {
      switch (quality) {
        case HsTextureQuality.Low:
        case HsTextureQuality.Medium:
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
          break;
        case HsTextureQuality.High:
        case HsTextureQuality.Highest:
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
          break;
        default:
          const neverQuality: never = quality;
          throw new Error(`ERROR: TextureUtils - Unknown texture quality value: ${neverQuality}`);
      }
    }
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  }
}
